import { useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing hover-based menu open/close behavior
 * Provides timer-based delayed closing when mouse leaves the menu area
 * 
 * @param delay - Delay in milliseconds before closing menu (default: 250ms)
 * @returns Object with cancelClose and scheduleClose functions
 * 
 * @example
 * const { cancelClose, scheduleClose } = useHoverMenu(250);
 * 
 * <Box
 *   onMouseEnter={cancelClose}
 *   onMouseLeave={scheduleClose}
 * >
 *   Menu content
 * </Box>
 */
export const useHoverMenu = (delay: number = 250) => {
	const closeTimer = useRef<number | null>(null);

	/**
	 * Cancel any scheduled close operation
	 * Call this when mouse enters the menu area
	 */
	const cancelClose = useCallback(() => {
		if (closeTimer.current) {
			window.clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);

	/**
	 * Schedule menu to close after delay
	 * Call this when mouse leaves the menu area
	 * Returns a callback to set the menu closed
	 */
	const scheduleClose = useCallback(
		(onClose: () => void) => {
			if (closeTimer.current) {
				window.clearTimeout(closeTimer.current);
			}
			closeTimer.current = window.setTimeout(() => {
				onClose();
			}, delay);
		},
		[delay],
	);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (closeTimer.current) {
				window.clearTimeout(closeTimer.current);
			}
		};
	}, []);

	return { cancelClose, scheduleClose, closeTimer };
};

